import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Spin,
  Alert,
  Typography,
  Statistic,
  Progress,
  Tag,
  Table,
  Tabs,
  Space,
  Tooltip,
  Empty,
  message
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  BugOutlined
} from '@ant-design/icons';
import {
  generateComprehensiveAnalytics,
  TIME_PERIODS,
  MaterialUtilizationAnalytics,
  ScheduleOptimizationAnalytics,
  HybridMaterialFinancialAnalytics
} from '../../utils/analyticsEngine';
import UtilizationProjections from './UtilizationProjections';
import UtilizationDiagnostic from './UtilizationDiagnostic';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

/**
 * Analytics Dashboard Component
 * Displays comprehensive analytics for material utilization and schedule optimization
 */
const AnalyticsDashboard = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(TIME_PERIODS.LAST_30_DAYS);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);

  // Load analytics data
  useEffect(() => {
    loadAnalyticsData();
  }, [selectedPeriod, customDateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📊 Loading analytics data...');
      
      const data = await generateComprehensiveAnalytics(selectedPeriod, customDateRange);
      setAnalyticsData(data);
      
      console.log('✅ Analytics data loaded successfully');
      
    } catch (error) {
      console.error('❌ Error loading analytics data:', error);
      setError('Failed to load analytics data. Please try again.');
      message.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Handle period change
  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period);
    if (period !== TIME_PERIODS.CUSTOM) {
      setCustomDateRange(null);
    }
  }, []);

  // Handle custom date range change
  const handleCustomDateRangeChange = useCallback((dates) => {
    if (dates && dates.length === 2) {
      setCustomDateRange({
        startDate: dates[0].toDate(),
        endDate: dates[1].toDate()
      });
    } else {
      setCustomDateRange(null);
    }
  }, []);

  // Refresh analytics data
  const handleRefresh = useCallback(() => {
    loadAnalyticsData();
  }, [selectedPeriod, customDateRange]);

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'declining':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      case 'stable':
        return <MinusOutlined style={{ color: '#1890ff' }} />;
      default:
        return <MinusOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  // Get insight icon
  const getInsightIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'warning':
        return <WarningOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'info':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ff4d4f';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#d9d9d9';
    }
  };

  // Render overview tab
  const renderOverviewTab = () => {
    if (!analyticsData) return null;

    const { utilization, scheduleOptimization, summary } = analyticsData;

    return (
      <div>
        {/* Key Metrics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Inventory Items"
                value={utilization.metrics.totalInventoryItems}
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Availability Rate"
                value={utilization.metrics.availabilityRate}
                suffix="%"
                prefix={getTrendIcon(utilization.trends.availabilityTrend)}
                valueStyle={{ 
                  color: utilization.metrics.availabilityRate > 80 ? '#52c41a' : '#faad14' 
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Utilization Rate"
                value={utilization.metrics.utilizationRate}
                suffix="%"
                prefix={getTrendIcon(utilization.trends.utilizationTrend)}
                valueStyle={{ 
                  color: utilization.metrics.utilizationRate > 70 ? '#52c41a' : '#faad14' 
                }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Schedule Completion"
                value={scheduleOptimization.metrics.completionRate}
                suffix="%"
                prefix={<LineChartOutlined />}
                valueStyle={{ 
                  color: scheduleOptimization.metrics.completionRate > 80 ? '#52c41a' : '#ff4d4f' 
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* Summary Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={8}>
            <Card title="Summary" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Total Insights:</Text>
                  <Text strong>{summary.totalInsights}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>High Priority Issues:</Text>
                  <Text strong style={{ color: summary.highPriorityIssues > 0 ? '#ff4d4f' : '#52c41a' }}>
                    {summary.highPriorityIssues}
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Recommendations:</Text>
                  <Text strong>{summary.totalRecommendations}</Text>
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Material Health" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text>Damage Rate</Text>
                  <Progress 
                    percent={utilization.metrics.damageRate} 
                    size="small"
                    strokeColor={utilization.metrics.damageRate > 15 ? '#ff4d4f' : '#52c41a'}
                    format={(percent) => `${percent}%`}
                  />
                </div>
                <div>
                  <Text>Idle Time</Text>
                  <Progress 
                    percent={utilization.metrics.idleTimePercentage} 
                    size="small"
                    strokeColor={utilization.metrics.idleTimePercentage > 30 ? '#faad14' : '#52c41a'}
                    format={(percent) => `${percent}%`}
                  />
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Schedule Performance" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text>Cancellation Rate</Text>
                  <Progress 
                    percent={scheduleOptimization.metrics.cancellationRate} 
                    size="small"
                    strokeColor={scheduleOptimization.metrics.cancellationRate > 15 ? '#ff4d4f' : '#52c41a'}
                    format={(percent) => `${percent}%`}
                  />
                </div>
                <div>
                  <Text>Delay Rate</Text>
                  <Progress 
                    percent={scheduleOptimization.metrics.delayRate} 
                    size="small"
                    strokeColor={scheduleOptimization.metrics.delayRate > 20 ? '#ff4d4f' : '#52c41a'}
                    format={(percent) => `${percent}%`}
                  />
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Top Insights */}
        <Card title="Key Insights" style={{ marginBottom: 24 }}>
          {[...utilization.insights, ...scheduleOptimization.insights]
            .sort((a, b) => {
              const priorityOrder = { high: 3, medium: 2, low: 1 };
              return priorityOrder[b.priority] - priorityOrder[a.priority];
            })
            .slice(0, 5)
            .map((insight, index) => (
              <Alert
                key={index}
                message={insight.title}
                description={insight.message}
                type={insight.type}
                icon={getInsightIcon(insight.type)}
                style={{ marginBottom: 8 }}
                action={
                  <Tag color={getPriorityColor(insight.priority)}>
                    {insight.priority.toUpperCase()}
                  </Tag>
                }
              />
            ))}
        </Card>
      </div>
    );
  };

  // Render material utilization tab
  const renderMaterialUtilizationTab = () => {
    if (!analyticsData?.utilization) return null;

    const { metrics, materialBreakdown, insights, recommendations } = analyticsData.utilization;

    // Prepare material breakdown table data
    const materialTableData = Object.entries(materialBreakdown).map(([type, data]) => ({
      key: type,
      materialType: type,
      total: data.total,
      available: data.available,
      deployed: data.deployed,
      damaged: data.damaged,
      requests: data.requests,
      utilizationRate: data.total > 0 ? ((data.deployed / data.total) * 100).toFixed(1) : 0
    }));

    const materialColumns = [
      {
        title: 'Material Type',
        dataIndex: 'materialType',
        key: 'materialType',
        sorter: (a, b) => a.materialType.localeCompare(b.materialType)
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        sorter: (a, b) => a.total - b.total
      },
      {
        title: 'Available',
        dataIndex: 'available',
        key: 'available',
        sorter: (a, b) => a.available - b.available,
        render: (value) => <Text style={{ color: '#52c41a' }}>{value}</Text>
      },
      {
        title: 'Deployed',
        dataIndex: 'deployed',
        key: 'deployed',
        sorter: (a, b) => a.deployed - b.deployed,
        render: (value) => <Text style={{ color: '#1890ff' }}>{value}</Text>
      },
      {
        title: 'Damaged',
        dataIndex: 'damaged',
        key: 'damaged',
        sorter: (a, b) => a.damaged - b.damaged,
        render: (value) => <Text style={{ color: '#ff4d4f' }}>{value}</Text>
      },
      {
        title: 'Requests',
        dataIndex: 'requests',
        key: 'requests',
        sorter: (a, b) => a.requests - b.requests
      },
      {
        title: 'Utilization',
        dataIndex: 'utilizationRate',
        key: 'utilizationRate',
        sorter: (a, b) => parseFloat(a.utilizationRate) - parseFloat(b.utilizationRate),
        render: (value) => (
          <Progress 
            percent={parseFloat(value)} 
            size="small"
            strokeColor={parseFloat(value) > 70 ? '#52c41a' : '#faad14'}
            format={(percent) => `${percent}%`}
          />
        )
      }
    ];

    return (
      <div>
        {/* Utilization Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Availability Rate"
                value={metrics.availabilityRate}
                suffix="%"
                valueStyle={{ color: metrics.availabilityRate > 80 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Utilization Rate"
                value={metrics.utilizationRate}
                suffix="%"
                valueStyle={{ color: metrics.utilizationRate > 70 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic
                title="Turnover Rate"
                value={metrics.turnoverRate}
                suffix="/year"
                precision={1}
                valueStyle={{ color: metrics.turnoverRate > 4 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Material Breakdown Table */}
        <Card title="Material Type Breakdown" style={{ marginBottom: 24 }}>
          <Table
            dataSource={materialTableData}
            columns={materialColumns}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: true }}
          />
        </Card>

        {/* Insights and Recommendations */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Material Insights" size="small">
              {insights.length > 0 ? (
                insights.map((insight, index) => (
                  <Alert
                    key={index}
                    message={insight.title}
                    description={insight.message}
                    type={insight.type}
                    size="small"
                    style={{ marginBottom: 8 }}
                    action={
                      <Tag color={getPriorityColor(insight.priority)} size="small">
                        {insight.priority.toUpperCase()}
                      </Tag>
                    }
                  />
                ))
              ) : (
                <Empty description="No insights available" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Recommendations" size="small">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Text strong>{rec.title}</Text>
                        <Paragraph style={{ margin: '4px 0', fontSize: '12px' }}>
                          {rec.description}
                        </Paragraph>
                        <Space size="small">
                          <Tag size="small">Impact: {rec.estimatedImpact}</Tag>
                          <Tag size="small">Timeline: {rec.timeframe}</Tag>
                        </Space>
                      </div>
                      <Tag color={getPriorityColor(rec.priority)} size="small">
                        {rec.priority.toUpperCase()}
                      </Tag>
                    </div>
                  </Card>
                ))
              ) : (
                <Empty description="No recommendations available" />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Render financial analytics tab
  const renderFinancialAnalyticsTab = () => {
    if (!analyticsData?.hybridMaterialFinancial) return null;

    const { materialData, financialMetrics, insights, recommendations, dataSourceBreakdown } = analyticsData.hybridMaterialFinancial;

    // Prepare material revenue table data
    const materialRevenueData = Object.entries(materialData.materials).map(([materialType, data]) => ({
      key: materialType,
      materialType,
      totalQuantity: data.totalQuantity,
      totalRevenue: data.totalRevenue,
      averagePrice: data.averagePrice,
      revenuePerUnit: data.revenuePerUnit,
      profitMargin: data.profitMargin,
      invoiceCount: data.invoiceCount,
      fromMaterialLists: data.fromMaterialLists,
      fromLineItems: data.fromLineItems,
      dataSource: data.dataSource
    }));

    const materialRevenueColumns = [
      {
        title: 'Material Type',
        dataIndex: 'materialType',
        key: 'materialType',
        sorter: (a, b) => a.materialType.localeCompare(b.materialType),
        render: (text) => <Text strong>{text.replace('_', ' ')}</Text>
      },
      {
        title: 'Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        sorter: (a, b) => a.totalQuantity - b.totalQuantity,
        render: (value) => <Text>{value.toLocaleString()}</Text>
      },
      {
        title: 'Revenue',
        dataIndex: 'totalRevenue',
        key: 'totalRevenue',
        sorter: (a, b) => a.totalRevenue - b.totalRevenue,
        render: (value) => <Text strong style={{ color: '#52c41a' }}>${value.toLocaleString()}</Text>
      },
      {
        title: 'Avg Price',
        dataIndex: 'averagePrice',
        key: 'averagePrice',
        sorter: (a, b) => a.averagePrice - b.averagePrice,
        render: (value) => <Text>${value.toFixed(2)}</Text>
      },
      {
        title: 'Profit Margin',
        dataIndex: 'profitMargin',
        key: 'profitMargin',
        sorter: (a, b) => a.profitMargin - b.profitMargin,
        render: (value) => (
          <Progress 
            percent={value} 
            size="small"
            strokeColor={value > 30 ? '#52c41a' : value > 20 ? '#faad14' : '#ff4d4f'}
            format={(percent) => `${percent.toFixed(1)}%`}
          />
        )
      },
      {
        title: 'Invoices',
        dataIndex: 'invoiceCount',
        key: 'invoiceCount',
        sorter: (a, b) => a.invoiceCount - b.invoiceCount
      },
      {
        title: 'Data Source',
        dataIndex: 'dataSource',
        key: 'dataSource',
        render: (value, record) => (
          <Tooltip title={`Material Lists: ${record.fromMaterialLists}, Line Items: ${record.fromLineItems}`}>
            <Tag color={value === 'hybrid' ? '#1890ff' : '#d9d9d9'}>
              {value.toUpperCase()}
            </Tag>
          </Tooltip>
        )
      }
    ];

    return (
      <div>
        {/* Financial Overview */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={financialMetrics.totalRevenue}
                prefix="$"
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Payment Rate"
                value={financialMetrics.paymentRate}
                suffix="%"
                precision={1}
                valueStyle={{ color: financialMetrics.paymentRate > 80 ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Revenue"
                value={financialMetrics.pendingRevenue}
                prefix="$"
                valueStyle={{ color: financialMetrics.pendingRevenue > 10000 ? '#ff4d4f' : '#faad14' }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Invoice Value"
                value={financialMetrics.averageInvoiceValue}
                prefix="$"
                precision={0}
                valueStyle={{ color: '#1890ff' }}
                formatter={(value) => value.toLocaleString()}
              />
            </Card>
          </Col>
        </Row>

        {/* Data Quality Overview */}
        <Card title="Data Source Analysis" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={dataSourceBreakdown.materialListPercentage}
                  format={(percent) => `${percent.toFixed(1)}%`}
                  strokeColor="#52c41a"
                />
                <div style={{ marginTop: 8 }}>
                  <Text strong>Material List Coverage</Text>
                  <br />
                  <Text type="secondary">
                    {dataSourceBreakdown.materialListCount} of {dataSourceBreakdown.total} invoices
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={dataSourceBreakdown.lineItemPercentage}
                  format={(percent) => `${percent.toFixed(1)}%`}
                  strokeColor="#faad14"
                />
                <div style={{ marginTop: 8 }}>
                  <Text strong>Line Item Coverage</Text>
                  <br />
                  <Text type="secondary">
                    {dataSourceBreakdown.lineItemCount} of {dataSourceBreakdown.total} invoices
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ textAlign: 'center' }}>
                <Progress
                  type="circle"
                  percent={dataSourceBreakdown.migrationProgress}
                  format={(percent) => `${percent.toFixed(1)}%`}
                  strokeColor="#1890ff"
                />
                <div style={{ marginTop: 8 }}>
                  <Text strong>Migration Progress</Text>
                  <br />
                  <Text type="secondary">
                    Towards full material list adoption
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Top Performing Materials */}
        <Card title="Top Performing Materials" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            {financialMetrics.topMaterials.slice(0, 3).map((material, index) => (
              <Col xs={24} md={8} key={material.materialType}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', marginBottom: 8 }}>
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                  </div>
                  <Text strong>{material.materialType.replace('_', ' ')}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Statistic
                      value={material.revenue}
                      prefix="$"
                      valueStyle={{ fontSize: '18px', color: '#52c41a' }}
                      formatter={(value) => value.toLocaleString()}
                    />
                    <Text type="secondary">
                      {material.quantity} units • ${material.revenuePerUnit.toFixed(2)}/unit
                    </Text>
                    {material.materialEfficiency && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Material Efficiency: {material.materialEfficiency.toFixed(2)}x
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Most Material Efficient Products */}
        <Card title="Most Material Efficient Products" style={{ marginBottom: 24 }}>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Products ranked by revenue generated per dollar of material cost (higher is better for profitability)
          </Paragraph>
          <Row gutter={[16, 16]}>
            {financialMetrics.topMaterials.filter(material => material.materialEfficiency).slice(0, 3).map((product, index) => (
              <Col xs={24} md={8} key={product.materialType}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', marginBottom: 8 }}>
                    {index === 0 ? '⚡' : index === 1 ? '💡' : '🔧'}
                  </div>
                  <Text strong style={{ fontSize: '14px' }}>{product.materialType}</Text>
                  <div style={{ marginTop: 8 }}>
                    <Statistic
                      value={product.materialEfficiency}
                      suffix="x"
                      precision={2}
                      valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                    />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ${product.revenuePerUnit.toFixed(0)}/unit revenue<br/>
                      ~${product.estimatedMaterialCost} material cost
                    </Text>
                  </div>
                  <div style={{ marginTop: 8, padding: '4px 8px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                    <Text style={{ fontSize: '11px' }}>
                      {product.quantity} units • ${product.revenue.toLocaleString()} total
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
          <Alert
            message="Material Efficiency Insight"
            description="Single Width scaffolds typically show higher material efficiency compared to Double Width scaffolds, as they use roughly half the materials but command proportionally higher pricing per material unit. This makes them more profitable from a material utilization perspective."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>

        {/* Revenue by Invoice Type */}
        <Card title="Revenue by Invoice Type" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            {Object.entries(financialMetrics.revenueByType).map(([type, revenue]) => (
              <Col xs={12} sm={8} md={6} key={type}>
                <Card size="small" style={{ textAlign: 'center' }}>
                  <Statistic
                    title={type.replace('_', ' ').toUpperCase()}
                    value={revenue}
                    prefix="$"
                    valueStyle={{ fontSize: '16px' }}
                    formatter={(value) => value.toLocaleString()}
                  />
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">
                      {((revenue / financialMetrics.totalRevenue) * 100).toFixed(1)}% of total
                    </Text>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Material Revenue Breakdown Table */}
        <Card title="Material Revenue Analysis" style={{ marginBottom: 24 }}>
          <Table
            dataSource={materialRevenueData}
            columns={materialRevenueColumns}
            pagination={{ pageSize: 10 }}
            size="small"
            scroll={{ x: true }}
            summary={(pageData) => {
              const totalRevenue = pageData.reduce((sum, record) => sum + record.totalRevenue, 0);
              const totalQuantity = pageData.reduce((sum, record) => sum + record.totalQuantity, 0);
              const avgMargin = pageData.reduce((sum, record) => sum + record.profitMargin, 0) / pageData.length;

              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell><Text strong>TOTAL</Text></Table.Summary.Cell>
                  <Table.Summary.Cell><Text strong>{totalQuantity.toLocaleString()}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell><Text strong style={{ color: '#52c41a' }}>${totalRevenue.toLocaleString()}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell><Text strong>${(totalRevenue / totalQuantity).toFixed(2)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell><Text strong>{avgMargin.toFixed(1)}%</Text></Table.Summary.Cell>
                  <Table.Summary.Cell />
                  <Table.Summary.Cell />
                </Table.Summary.Row>
              );
            }}
          />
        </Card>

        {/* Financial Insights and Recommendations */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Financial Insights" size="small">
              {insights.length > 0 ? (
                insights.map((insight, index) => (
                  <Alert
                    key={index}
                    message={insight.title}
                    description={insight.message}
                    type={insight.type}
                    size="small"
                    style={{ marginBottom: 8 }}
                    action={
                      <Tag color={getPriorityColor(insight.priority)} size="small">
                        {insight.priority.toUpperCase()}
                      </Tag>
                    }
                  />
                ))
              ) : (
                <Empty description="No financial insights available" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Financial Recommendations" size="small">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Text strong>{rec.title}</Text>
                        <Paragraph style={{ margin: '4px 0', fontSize: '12px' }}>
                          {rec.description}
                        </Paragraph>
                        <Space size="small">
                          <Tag size="small">Impact: {rec.estimatedImpact}</Tag>
                          <Tag size="small">Timeline: {rec.timeframe}</Tag>
                        </Space>
                      </div>
                      <Tag color={getPriorityColor(rec.priority)} size="small">
                        {rec.priority.toUpperCase()}
                      </Tag>
                    </div>
                  </Card>
                ))
              ) : (
                <Empty description="No financial recommendations available" />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Render schedule optimization tab
  const renderScheduleOptimizationTab = () => {
    if (!analyticsData?.scheduleOptimization) return null;

    const { metrics, scheduleEfficiency, conflictAnalysis, insights, recommendations } = analyticsData.scheduleOptimization;

    // Prepare job type efficiency data
    const jobTypeData = Object.entries(scheduleEfficiency.jobTypeEfficiency).map(([jobType, data]) => ({
      key: jobType,
      jobType,
      total: data.total,
      completed: data.completed,
      delayed: data.delayed,
      completionRate: data.total > 0 ? ((data.completed / data.total) * 100).toFixed(1) : 0,
      delayRate: data.total > 0 ? ((data.delayed / data.total) * 100).toFixed(1) : 0
    }));

    const jobTypeColumns = [
      {
        title: 'Job Type',
        dataIndex: 'jobType',
        key: 'jobType',
        render: (text) => <Text strong>{text.replace('_', ' ').toUpperCase()}</Text>
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        sorter: (a, b) => a.total - b.total
      },
      {
        title: 'Completed',
        dataIndex: 'completed',
        key: 'completed',
        sorter: (a, b) => a.completed - b.completed,
        render: (value) => <Text style={{ color: '#52c41a' }}>{value}</Text>
      },
      {
        title: 'Delayed',
        dataIndex: 'delayed',
        key: 'delayed',
        sorter: (a, b) => a.delayed - b.delayed,
        render: (value) => <Text style={{ color: '#ff4d4f' }}>{value}</Text>
      },
      {
        title: 'Completion Rate',
        dataIndex: 'completionRate',
        key: 'completionRate',
        sorter: (a, b) => parseFloat(a.completionRate) - parseFloat(b.completionRate),
        render: (value) => (
          <Progress 
            percent={parseFloat(value)} 
            size="small"
            strokeColor={parseFloat(value) > 80 ? '#52c41a' : '#ff4d4f'}
            format={(percent) => `${percent}%`}
          />
        )
      }
    ];

    return (
      <div>
        {/* Schedule Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Schedules"
                value={metrics.totalSchedules}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Completion Rate"
                value={metrics.completionRate}
                suffix="%"
                valueStyle={{ color: metrics.completionRate > 80 ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Cancellation Rate"
                value={metrics.cancellationRate}
                suffix="%"
                valueStyle={{ color: metrics.cancellationRate < 15 ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Average Lead Time"
                value={metrics.averageLeadTime}
                suffix="days"
                precision={1}
                valueStyle={{ color: metrics.averageLeadTime > 2 ? '#52c41a' : '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Job Type Efficiency */}
        <Card title="Job Type Performance" style={{ marginBottom: 24 }}>
          <Table
            dataSource={jobTypeData}
            columns={jobTypeColumns}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
        </Card>

        {/* Conflict Analysis */}
        <Card title="Schedule Conflicts" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Statistic
                title="Total Conflicts"
                value={conflictAnalysis.totalConflicts}
                valueStyle={{ color: conflictAnalysis.totalConflicts > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Col>
            <Col xs={24} md={8}>
              <Statistic
                title="Conflict Rate"
                value={conflictAnalysis.conflictRate}
                suffix="%"
                precision={1}
                valueStyle={{ color: conflictAnalysis.conflictRate > 10 ? '#ff4d4f' : '#52c41a' }}
              />
            </Col>
            <Col xs={24} md={8}>
              {conflictAnalysis.totalConflicts === 0 ? (
                <Alert
                  message="No Conflicts Detected"
                  type="success"
                  showIcon
                  style={{ height: '100%' }}
                />
              ) : (
                <Alert
                  message="Conflicts Detected"
                  description="Review schedule conflicts to optimize resource allocation"
                  type="warning"
                  showIcon
                />
              )}
            </Col>
          </Row>
        </Card>

        {/* Insights and Recommendations */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Schedule Insights" size="small">
              {insights.length > 0 ? (
                insights.map((insight, index) => (
                  <Alert
                    key={index}
                    message={insight.title}
                    description={insight.message}
                    type={insight.type}
                    size="small"
                    style={{ marginBottom: 8 }}
                    action={
                      <Tag color={getPriorityColor(insight.priority)} size="small">
                        {insight.priority.toUpperCase()}
                      </Tag>
                    }
                  />
                ))
              ) : (
                <Empty description="No insights available" />
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Optimization Recommendations" size="small">
              {recommendations.length > 0 ? (
                recommendations.map((rec, index) => (
                  <Card key={index} size="small" style={{ marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <Text strong>{rec.title}</Text>
                        <Paragraph style={{ margin: '4px 0', fontSize: '12px' }}>
                          {rec.description}
                        </Paragraph>
                        <Space size="small">
                          <Tag size="small">Impact: {rec.estimatedImpact}</Tag>
                          <Tag size="small">Timeline: {rec.timeframe}</Tag>
                        </Space>
                      </div>
                      <Tag color={getPriorityColor(rec.priority)} size="small">
                        {rec.priority.toUpperCase()}
                      </Tag>
                    </div>
                  </Card>
                ))
              ) : (
                <Empty description="No recommendations available" />
              )}
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Analytics Dashboard</Title>
        <Paragraph>
          Comprehensive analytics for material utilization and schedule optimization
        </Paragraph>
      </div>

      {/* Controls */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong>Time Period:</Text>
              <Select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                style={{ width: '100%', marginTop: 4 }}
              >
                <Option value={TIME_PERIODS.LAST_7_DAYS}>Last 7 Days</Option>
                <Option value={TIME_PERIODS.LAST_30_DAYS}>Last 30 Days</Option>
                <Option value={TIME_PERIODS.LAST_90_DAYS}>Last 90 Days</Option>
                <Option value={TIME_PERIODS.LAST_6_MONTHS}>Last 6 Months</Option>
                <Option value={TIME_PERIODS.LAST_YEAR}>Last Year</Option>
                <Option value={TIME_PERIODS.ALL_TIME}>All Time</Option>
                <Option value={TIME_PERIODS.CUSTOM}>Custom Range</Option>
              </Select>
            </div>
          </Col>
          
          {selectedPeriod === TIME_PERIODS.CUSTOM && (
            <Col xs={24} sm={12} md={8}>
              <div>
                <Text strong>Custom Date Range:</Text>
                <RangePicker
                  onChange={handleCustomDateRangeChange}
                  style={{ width: '100%', marginTop: 4 }}
                  format="DD/MM/YYYY"
                />
              </div>
            </Col>
          )}
          
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefresh}
                loading={loading}
              >
                Refresh
              </Button>
              <Button 
                icon={<DownloadOutlined />} 
                disabled={!analyticsData}
                onClick={() => {
                  // TODO: Implement export functionality
                  message.info('Export functionality coming soon');
                }}
              >
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Error State */}
      {error && (
        <Alert
          message="Error Loading Analytics"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading analytics data...</Text>
          </div>
        </div>
      )}

      {/* Analytics Content */}
      {!loading && analyticsData && (
        <Card>
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="Overview" key="overview">
              {renderOverviewTab()}
            </TabPane>
            <TabPane tab="Material Utilization" key="utilization">
              {renderMaterialUtilizationTab()}
            </TabPane>
            <TabPane tab="Schedule Optimization" key="schedules">
              {renderScheduleOptimizationTab()}
            </TabPane>
            <TabPane tab="Financial Analytics" key="financial">
              {renderFinancialAnalyticsTab()}
            </TabPane>
            <TabPane 
              tab={
                <span>
                  <ExperimentOutlined />
                  Utilization Projections
                </span>
              } 
              key="projections"
            >
              <UtilizationProjections 
                analyticsData={analyticsData} 
                loading={loading} 
              />
            </TabPane>
            <TabPane 
              tab={
                <span>
                  <BugOutlined />
                  Diagnostic
                </span>
              } 
              key="diagnostic"
            >
              <UtilizationDiagnostic />
            </TabPane>
          </Tabs>
        </Card>
      )}

      {/* Empty State */}
      {!loading && !analyticsData && !error && (
        <Card>
          <Empty
            description="No analytics data available"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={handleRefresh}>
              Load Analytics
            </Button>
          </Empty>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
